function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Container } from "../../components/container";
import { PageHeader, Ribbon, Card, Pill, Empty, Search, Filters, Delta, Pending } from "../../components/analytics-kit";
import PlunkDropDown from "../../components/plunk-dropdown";
import { ChartLoader } from "../../components/loaders";
import ServiceFactory from "../../service";
import { hasTenantFeature } from "../../functions/tenant-features";
import { num, usd } from "../sales/territory-common";

/*
 * Best Fit panel — Stage 2 (retail) of the Product Performance spec:
 * "given a store / audience / market → which catalog products fit,
 * especially ones not yet stocked?"
 *
 * Data: GET /v1/sales/best-fit/{range}?store=|region=|audience=&top=
 * (Service.Product 1.1.20 — demand-weighted visual-similarity ranker).
 * Rows carry {productId, description, fit, isNew} — NO title, so display
 * names/thumbnails are joined client-side from the stats list the parent
 * page already loads; unjoined rows fall back to a cleaned description
 * snippet. `isNew` = not currently sold in the selected context (the
 * discovery pick).
 *
 * Gated by the `bestFit` registry feature (default OFF — the MI-leak
 * lesson); retail tenants get it deliberately. Row drill-through to
 * Product Detail only when the tenant also has territoryRep.
 */

function fetchSalesApi(api) {
  const Service = ServiceFactory.getType(api, "product");
  const service = new Service();
  return service.getAsync();
}

// "\"The Bali Nightstand provides functional storage…" → "The Bali Nightstand provides functional storage…"
function cleanDescription(d) {
  return String(d || "").replace(/^"+/, "").replace(/""/g, '"').split(" SKU: ")[0];
}
class BestFitPanel extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "onContext", e => {
      const context = e.target.value;
      this.setState({
        context
      });
      this.loadRows(context);
    });
    _defineProperty(this, "openProduct", (p, title) => {
      if (!hasTenantFeature("territoryRep")) return;
      this.props.history.push({
        pathname: `/Territory/${this.props.range}/product/${encodeURIComponent(p.productId)}`,
        state: title ? {
          title
        } : undefined
      });
    });
    this.state = {
      contexts: null,
      context: "",
      rows: null,
      loading: false,
      error: false
    };
    this._alive = true;
  }
  componentDidMount() {
    if (!hasTenantFeature("bestFit")) return;
    this.loadContexts();
    this.loadRows("");
  }
  componentWillUnmount() {
    this._alive = false;
  }
  componentDidUpdate(prev) {
    if (!hasTenantFeature("bestFit")) return;
    if (prev.range !== this.props.range) {
      this.loadContexts();
      this.loadRows(this.state.context);
    } else if ((prev.category || "") !== (this.props.category || "")) {
      this.loadRows(this.state.context);
    }
  }
  async loadContexts() {
    try {
      const [stores, audiences] = await Promise.all([fetchSalesApi(`/sales/stores/${this.props.range}`), fetchSalesApi(`/sales/audiences/${this.props.range}`)]);
      if (!this._alive) return;
      this.setState({
        contexts: {
          stores: (stores || []).map(s => s.store).filter(Boolean),
          audiences: (audiences || []).map(a => a.audienceName).filter(Boolean)
        }
      });
    } catch (e) {
      if (this._alive) this.setState({
        contexts: {
          stores: [],
          audiences: []
        }
      });
    }
  }
  async loadRows(context) {
    this.setState({
      loading: true,
      error: false
    });
    let qs = "top=25";
    if (context.startsWith("store:")) qs += `&store=${encodeURIComponent(context.slice(6))}`;
    if (context.startsWith("audience:")) qs += `&audience=${encodeURIComponent(context.slice(9))}`;
    if (this.props.category) qs += `&category=${encodeURIComponent(this.props.category)}`;
    try {
      const rows = await fetchSalesApi(`/sales/best-fit/${this.props.range}?${qs}`);
      if (this._alive) this.setState({
        rows: rows || [],
        loading: false
      });
    } catch (e) {
      if (this._alive) this.setState({
        error: true,
        loading: false
      });
    }
  }
  render() {
    if (!hasTenantFeature("bestFit")) return null;
    const {
      contexts,
      context,
      rows,
      loading,
      error
    } = this.state;
    const statsById = this.props.statsById || {};
    const drill = hasTenantFeature("territoryRep");
    const maxFit = rows && rows.length ? Math.max(...rows.map(r => Number(r.fit) || 0)) : 0;
    const contextLabel = context ? context.startsWith("store:") ? `${context.slice(6)} store` : `“${context.slice(9)}” audience` : "all stores";
    return /*#__PURE__*/React.createElement(Card, {
      title: "Best fit",
      hint: "what to stock or push next",
      span: 12
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        marginBottom: "10px",
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "220px",
      value: context,
      onChange: v => this.onContext({
        target: {
          value: v
        }
      }),
      options: [["", "All stores"], ...(contexts && contexts.stores || []).map(s2 => [`store:${s2}`, `Store: ${s2}`])]
    }), /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "Catalog products ranked by similarity to what sells in ", contextLabel, ".", /*#__PURE__*/React.createElement("strong", null, " NEW"), " = not currently sold there \u2014 the discovery picks.")), error && /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, "Couldn't load best-fit rankings."), loading && /*#__PURE__*/React.createElement(ChartLoader, null), !loading && rows && /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Product"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Fit"), /*#__PURE__*/React.createElement("th", {
      style: {
        width: "160px"
      }
    }), /*#__PURE__*/React.createElement("th", null))), /*#__PURE__*/React.createElement("tbody", null, rows.map(p => {
      const st = statsById[p.productId];
      const title = p.title || st && st.title || null;
      const desc = cleanDescription(p.description);
      const fit = Number(p.fit) || 0;
      return /*#__PURE__*/React.createElement("tr", {
        key: p.productId,
        className: drill ? "clickable" : undefined,
        onClick: () => this.openProduct(p, title),
        title: desc.slice(0, 200)
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        style: {
          display: "inline-flex",
          alignItems: "center",
          gap: "10px"
        }
      }, st && st.imageLink ? /*#__PURE__*/React.createElement("img", {
        src: st.imageLink,
        alt: "",
        loading: "lazy",
        style: {
          width: "36px",
          height: "36px",
          objectFit: "contain",
          flex: "0 0 auto"
        }
      }) : null, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, title || desc.slice(0, 60) || p.productId), /*#__PURE__*/React.createElement("span", {
        className: "code"
      }, p.productId)))), /*#__PURE__*/React.createElement("td", {
        className: "r strong"
      }, fit.toFixed(1)), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("div", {
        style: {
          background: "rgba(45,115,245,.12)",
          borderRadius: "3px",
          height: "8px",
          width: "100%"
        }
      }, /*#__PURE__*/React.createElement("div", {
        style: {
          background: "#2d73f5",
          borderRadius: "3px",
          height: "8px",
          width: `${maxFit ? Math.max(4, Math.round(fit / maxFit * 100)) : 0}%`
        }
      }))), /*#__PURE__*/React.createElement("td", null, p.isNew ? /*#__PURE__*/React.createElement("span", {
        style: {
          background: "#12a594",
          color: "#fff",
          borderRadius: "3px",
          padding: "2px 7px",
          fontSize: "11px",
          fontWeight: 600,
          letterSpacing: ".04em"
        }
      }, "NEW") : null));
    }))), !loading && rows && rows.length === 0 && /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, "No rankings for this context (the similarity index may still be building)."));
  }
}
export default withRouter(BestFitPanel);